import cn from 'classnames';
import patternValidators from 'helpers/validators/pattern-validators';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { CustomFields } from 'views/Company/tabs/CustomFields/components/CustomFields/CustomFields';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  Input,
  InputGroup,
  MultipleSelect,
  PhoneNumberPicker,
  Select,
} from 'components/FormComponents';

export const NewEmployeeModal = ({
  visible,
  company = {},
  tags = [],
  teams = [],
  customFields = [],
  users = [],
  createEmployee = () => {},
  onClose = () => {},
}) => {
  const {
    register,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const team_id = useWatch({ control, name: 'team_id' });

  return (
    <AnimatedModal visible={visible} onClose={onClose} title="Add New Employee" small>
      <form onSubmit={handleSubmit(createEmployee)}>
        <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
          <div className="inline u-margin-bottom">
            <InputGroup title="First Name *">
              <Input
                name="first_name"
                placeholder="e.g. Brad"
                register={register}
                validators={{
                  required: true,
                  ...patternValidators.name,
                }}
                error={errors?.first_name?.type}
                inputProps={{ tabIndex: 1 }}
                dataTestId="create-check-candidate-first-name"
              />
            </InputGroup>
            <InputGroup title="Last Name *">
              <Input
                name="last_name"
                placeholder="e.g. Pitt"
                register={register}
                validators={{
                  required: true,
                  ...patternValidators.name,
                }}
                error={errors?.last_name?.type}
                inputProps={{ tabIndex: 2 }}
                dataTestId="create-check-candidate-last-name"
              />
            </InputGroup>
          </div>
          <InputGroup title="Email Address *" className="u-margin-bottom--large">
            <Input
              name="email"
              placeholder="e.g. brad@checkmate.tech"
              register={register}
              validators={{
                required: true,
                ...patternValidators.email,
              }}
              error={errors?.email?.type}
              inputProps={{ tabIndex: 3 }}
              dataTestId="create-check-candidate-email"
            />
          </InputGroup>
          <InputGroup title="Contact Number" className="u-margin-bottom--large">
            <Controller
              control={control}
              name="contact_number"
              render={({ field: { onChange, value } }) => (
                <PhoneNumberPicker
                  value={value}
                  onChange={onChange}
                  tabIndex={4}
                  defaultCountry={company?.country}
                  dataTestId="create-check-candidate-contact-number"
                />
              )}
            />
          </InputGroup>
          <InputGroup title="Job Title" className="u-margin-bottom--large">
            <Input
              name="job_role"
              placeholder="Mastermind"
              register={register}
              inputProps={{ tabIndex: 5 }}
              dataTestId="create-check-candidate-job-role"
            />
          </InputGroup>
          <InputGroup title="Department" className="u-margin-bottom--large">
            <Input
              name="department"
              placeholder="Oceans 11"
              register={register}
              inputProps={{ tabIndex: 6 }}
              dataTestId="create-check-candidate-department"
            />
          </InputGroup>
          <InputGroup title="Reference Code" className="u-margin-bottom--large">
            <Input
              name="reference_code"
              placeholder="REFCODE12"
              register={register}
              inputProps={{ tabIndex: 7 }}
              dataTestId="create-check-candidate-reference-code"
            />
          </InputGroup>
          {teams.length > 1 && (
            <InputGroup title="Team" className="u-margin-bottom--large">
              <Select
                name="team_id"
                placeholder="Team"
                register={register}
                value={team_id}
                useDefault>
                {teams.map(team => (
                  <Select.Item key={team.id} value={team.id}>
                    {team.name}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
          )}
        </div>
        {tags?.length > 0 && (
          <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
            <div className="u-width-100">
              <div className={cn('u-bold')}>Tags</div>
              <InputGroup title="Tags to be added to the candidate. This can be updated at any time.">
                <Controller
                  control={control}
                  name={`tag_ids`}
                  placeholder="Select..."
                  render={({ field: { onChange, value } }) => (
                    <MultipleSelect value={value} onChange={onChange} placeholder="Select...">
                      {tags.map(tag => (
                        <MultipleSelect.Item key={tag.id} value={tag.id}>
                          {tag.name}
                        </MultipleSelect.Item>
                      ))}
                    </MultipleSelect>
                  )}
                />
              </InputGroup>
            </div>
          </div>
        )}
        <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
          <div className="u-width-100">
            <div className={cn('u-bold')}>Candidate Assignment</div>
            <InputGroup title="The user who is assigned to manage the candidate and receive notifications. This can be updated at any time.">
              <Select
                name="user_id"
                placeholder="User"
                register={register}
                value={watch('user_id')}
                useDefault
                useSearch>
                {(users || []).map(user => (
                  <Select.Item key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} • {user.email}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
          </div>
        </div>
        {customFields.length > 0 && (
          <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
            <div className="u-width-100">
              <CustomFields
                fields={customFields}
                register={register}
                control={control}
                watch={watch}
              />
            </div>
          </div>
        )}
        <Button submit className="u-width-100 u-margin-top--large">
          Create
        </Button>
      </form>
    </AnimatedModal>
  );
};
